/**
 * 通用「外观」设置：填充、描边（与 ElementAppearance.fills / strokes 对应，供属性面板组合接入）。
 */
import { useState, type ChangeEvent, type ComponentType, type ReactNode } from 'react';
import { Button, InputGroup, ListBox, Popover, Select, cn } from '@heroui/react';
import { CellSlider } from '@heroui-pro/react';
import { NumberStepper } from '@heroui-pro/react/number-stepper';
import { ColorPickerField } from '@/components/custom';
import {
  IconAdjustments,
  IconBorderCorners,
  IconMinus,
  IconPlus,
  IconRadiusBottomLeft,
  IconRadiusBottomRight,
  IconRadiusTopLeft,
  IconRadiusTopRight,
} from '@tabler/icons-react';
import type { CornerRadius, ElementAppearance, Stroke } from '@/components/canvas/document/publicTypes';
import {
  DEFAULT_APPEARANCE_SHADOW,
  appearanceShadowToEffect,
  shadowEffectToAppearanceValue,
  type AppearanceShadowValue,
} from '@/components/canvas/shared/utils/geometry/appearanceShadow';
import { resolveCornerRadiiPx } from '@/components/canvas/shared/utils/geometry/cornerRadius';
import {
  blurRadius,
  firstFill,
  firstShadow,
  firstStroke,
  withBlurFilter,
  withFirstFill,
  withFirstStroke,
} from '@/components/canvas/elements/model';
import { ShadowSettingsPopover } from './ShadowSettingsPopover';

export type { AppearanceShadowValue } from '@/components/canvas/shared/utils/geometry/appearanceShadow';
export { DEFAULT_APPEARANCE_SHADOW } from '@/components/canvas/shared/utils/geometry/appearanceShadow';

export const SHADOW_TYPE_OPTIONS = [
  { value: 'drop' as const, label: '外阴影' },
  { value: 'inner' as const, label: '内阴影' },
];

/** 描边线型（与 Stroke.style 一致） */
export type StrokeStyle = 'solid' | 'dashed' | 'dotted' | 'double';

export const STROKE_STYLE_OPTIONS: { value: StrokeStyle; label: string }[] = [
  { value: 'solid', label: '实线' },
  { value: 'dashed', label: '虚线' },
  { value: 'dotted', label: '点线' },
  { value: 'double', label: '双实线' },
];

export type StrokePlacement = NonNullable<Stroke['placement']>;

export const STROKE_PLACEMENT_OPTIONS: { value: StrokePlacement; label: string }[] = [
  { value: 'center', label: '居中' },
  { value: 'inside', label: '内部' },
  { value: 'outside', label: '外部' },
];

/** 单条描边在面板中的可编辑字段 */
export interface AppearanceStrokeValue {
  color: string;
  width: number;
  style?: StrokeStyle;
  placement?: StrokePlacement;
  /** 描边不透明度 0–1 */
  opacity?: number;
}

export interface AppearanceSettingsValue {
  /** 纯色填充；undefined 表示无填充层 */
  fillColor?: string;
  /** 主描边；undefined 表示无描边层 */
  stroke?: AppearanceStrokeValue;
  /** 圆角；undefined 表示未设置圆角 */
  radius?: CornerRadius;
  /** 整体不透明度 0–1；undefined 表示未展开编辑（渲染时通常为 1） */
  opacity?: number;
  /** 主阴影；undefined 表示无阴影 */
  shadow?: AppearanceShadowValue;
  /** 高斯模糊半径（px）；undefined 表示未设置模糊 */
  blurPx?: number;
}

export interface AppearanceSettingsProps {
  className?: string;
  /** 区块总标题，默认「外观」 */
  title?: string;
  value: AppearanceSettingsValue;
  onChange: (patch: Partial<AppearanceSettingsValue>) => void;
  fillColorDefaultValue?: string;
  strokeColorDefaultValue?: string;
  strokeWidthDefaultValue?: number;
  strokeStyleDefaultValue?: StrokeStyle;
  strokePlacementDefaultValue?: StrokePlacement;
  strokeOpacityDefaultValue?: number;
  /** 线宽 NumberStepper 上限 */
  strokeWidthMax?: number;
  /** 添加圆角时的默认半径（px） */
  radiusDefaultValue?: number;
  /** 点击「+」添加透明度时的默认值 */
  opacityAddDefaultValue?: number;
  /** 透明度滑块步进 */
  opacityStep?: number;
  /** 点击「+」添加阴影时的默认值 */
  shadowAddDefaultValue?: AppearanceShadowValue;
  /** 点击「+」添加模糊时的默认值（px） */
  blurAddDefaultValue?: number;
  /** 模糊滑块上限（px） */
  blurMax?: number;
  /** 模糊滑块步进（px） */
  blurStep?: number;
}

const DEFAULT_STROKE: AppearanceStrokeValue = {
  color: '#6E6E6E',
  width: 1,
  style: 'solid',
  placement: 'inside',
  opacity: 1,
};

type CornerRadiusKey = 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function hasPerCornerRadius(radius: CornerRadius): boolean {
  return (
    radius.topLeft !== undefined ||
    radius.topRight !== undefined ||
    radius.bottomRight !== undefined ||
    radius.bottomLeft !== undefined
  );
}

function isUniformCornerRadius(radius: CornerRadius): boolean {
  if (radius.all !== undefined && !hasPerCornerRadius(radius)) return true;
  const { tl, tr, br, bl } = resolveCornerRadiiPx({ appearance: { radius } });
  return tl === tr && tr === br && br === bl;
}

function expandCornerRadius(radius: CornerRadius): CornerRadius {
  const { tl, tr, br, bl } = resolveCornerRadiiPx({ appearance: { radius } });
  return { topLeft: tl, topRight: tr, bottomRight: br, bottomLeft: bl };
}

function collapseCornerRadius(radius: CornerRadius): CornerRadius {
  const { tl, tr, br, bl } = resolveCornerRadiiPx({ appearance: { radius } });
  if (tl === tr && tr === br && br === bl) return { all: tl };
  return { all: Math.max(tl, tr, br, bl) };
}

function uniformCornerRadiusValue(radius: CornerRadius): number {
  const { tl, tr, br, bl } = resolveCornerRadiiPx({ appearance: { radius } });
  if (radius.all !== undefined && !hasPerCornerRadius(radius)) return Math.max(0, radius.all);
  if (tl === tr && tr === br && br === bl) return tl;
  return tl;
}

function clampOpacity(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function clampBlur(value: number, max: number): number {
  return Math.min(max, Math.max(0, Math.round(value)));
}

function strokeLinePreview(style: StrokeStyle) {
  return (
    <span
      className="inline-block w-10 shrink-0 border-t border-current opacity-80"
      style={{
        borderTopWidth: style === 'double' ? 2 : 1,
        borderTopStyle: style,
      }}
      aria-hidden
    />
  );
}

function AppearanceFieldRow({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex min-w-0 items-center gap-2', className)}>
      <label className="w-9 shrink-0 text-xs font-normal text-muted">{label}</label>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

function CornerRadiusInput({
  ariaLabel,
  Icon,
  value,
  onChange,
}: {
  ariaLabel: string;
  Icon: ComponentType<{ className?: string; stroke?: number }>;
  value: number;
  onChange: (next: number) => void;
}) {
  return (
    <InputGroup aria-label={ariaLabel} variant="secondary">
      <InputGroup.Prefix>
        <Icon stroke={1.5} aria-hidden />
      </InputGroup.Prefix>
      <InputGroup.Input
        type="number"
        min={0}
        value={value}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          onChange(Math.max(0, Math.round(num(e.target.value))))
        }
        className={cn('w-full text-left')}
      />
    </InputGroup>
  );
}

/** 从元素 appearance 读出面板值 */
export function appearanceValueFromAppearance(appearance: ElementAppearance): AppearanceSettingsValue {
  const fill = firstFill(appearance);
  const stroke = firstStroke(appearance);
  const radius = appearance.radius;
  return {
    fillColor: fill?.type === 'solid' ? fill.color : undefined,
    stroke: stroke
      ? {
          color: stroke.color,
          width: stroke.width,
          style: stroke.style,
          placement: stroke.placement,
          opacity: stroke.opacity ?? 1,
        }
      : undefined,
    radius:
      radius &&
      (radius.all !== undefined ||
        radius.topLeft !== undefined ||
        radius.topRight !== undefined ||
        radius.bottomRight !== undefined ||
        radius.bottomLeft !== undefined)
        ? radius
        : undefined,
    opacity: appearance.opacity !== 1 ? appearance.opacity : undefined,
    shadow: (() => {
      const s = firstShadow(appearance);
      return s ? shadowEffectToAppearanceValue(s) : undefined;
    })(),
    blurPx: (() => {
      const r = blurRadius({ appearance });
      return r != null && r > 0 ? r : undefined;
    })(),
  };
}

/** 将面板值转为 ElementAppearance 补丁（fills / strokes / radius / opacity / shadows / filters） */
export function appearanceValueToAppearancePatch(
  value: AppearanceSettingsValue,
  currentAppearance: ElementAppearance,
): Pick<ElementAppearance, 'fills' | 'strokes' | 'radius' | 'opacity' | 'shadows' | 'filters'> {
  const fallback: Stroke = {
    color: DEFAULT_STROKE.color,
    width: DEFAULT_STROKE.width,
    style: DEFAULT_STROKE.style,
    placement: DEFAULT_STROKE.placement,
    opacity: DEFAULT_STROKE.opacity,
  };
  return {
    fills: withFirstFill(value.fillColor),
    strokes: value.stroke
      ? withFirstStroke(firstStroke(currentAppearance), value.stroke, fallback)
      : undefined,
    radius: value.radius,
    opacity: value.opacity !== undefined ? clampOpacity(value.opacity) : 1,
    shadows: value.shadow ? [appearanceShadowToEffect(value.shadow)] : undefined,
    filters:
      value.blurPx !== undefined
        ? withBlurFilter(currentAppearance.filters, clampBlur(value.blurPx, 50))
        : withBlurFilter(currentAppearance.filters, 0),
  };
}

export function AppearanceSettings({
  className,
  title = '外观',
  value,
  onChange,
  fillColorDefaultValue = '#FFFFFF',
  strokeColorDefaultValue = '#6E6E6E',
  strokeWidthDefaultValue = 1,
  strokeStyleDefaultValue = 'solid',
  strokePlacementDefaultValue = 'inside',
  strokeOpacityDefaultValue = 1,
  strokeWidthMax = 100,
  radiusDefaultValue = 0,
  opacityAddDefaultValue = 0.5,
  opacityStep = 0.1,
  shadowAddDefaultValue = DEFAULT_APPEARANCE_SHADOW,
  blurAddDefaultValue = 10,
  blurMax = 50,
  blurStep = 1,
}: AppearanceSettingsProps) {
  const hasFill = value.fillColor !== undefined;
  const hasStroke = value.stroke !== undefined;
  const hasRadius = value.radius !== undefined;
  const hasOpacity = value.opacity !== undefined;
  const hasShadow = value.shadow !== undefined;
  const hasBlur = value.blurPx !== undefined;
  const opacity = value.opacity ?? opacityAddDefaultValue;
  const shadow = value.shadow;
  const blurPx = value.blurPx ?? blurAddDefaultValue;
  const radius = value.radius;

  const [cornersExpanded, setCornersExpanded] = useState(() =>
    radius ? !isUniformCornerRadius(radius) : false,
  );

  const stroke = value.stroke;
  const currentStrokeStyle =
    stroke?.style && STROKE_STYLE_OPTIONS.some((o) => o.value === stroke.style)
      ? stroke.style
      : strokeStyleDefaultValue;
  const strokeStyleLabel =
    STROKE_STYLE_OPTIONS.find((o) => o.value === currentStrokeStyle)?.label ?? '实线';

  const currentPlacement =
    stroke?.placement && STROKE_PLACEMENT_OPTIONS.some((o) => o.value === stroke.placement)
      ? stroke.placement
      : strokePlacementDefaultValue;
  const placementLabel =
    STROKE_PLACEMENT_OPTIONS.find((o) => o.value === currentPlacement)?.label ?? '内部';

  const addStroke = () => {
    onChange({
      stroke: {
        color: strokeColorDefaultValue,
        width: strokeWidthDefaultValue,
        style: strokeStyleDefaultValue,
        placement: strokePlacementDefaultValue,
        opacity: strokeOpacityDefaultValue,
      },
    });
  };

  const patchStroke = (patch: Partial<AppearanceStrokeValue>) => {
    if (!stroke) return;
    onChange({ stroke: { ...stroke, ...patch } });
  };

  const patchShadow = (patch: Partial<AppearanceShadowValue>) => {
    if (!shadow) return;
    onChange({ shadow: { ...shadow, ...patch } });
  };

  const patchCorner = (key: CornerRadiusKey, next: number) => {
    if (!radius) return;
    const expanded = expandCornerRadius(radius);
    onChange({ radius: { ...expanded, [key]: next } });
  };

  const toggleCornersExpanded = () => {
    if (!radius) return;
    if (cornersExpanded) {
      setCornersExpanded(false);
      onChange({ radius: collapseCornerRadius(radius) });
      return;
    }
    setCornersExpanded(true);
    onChange({ radius: expandCornerRadius(radius) });
  };

  const resolvedCorners = radius ? resolveCornerRadiiPx({ appearance: { radius } }) : null;

  return (
    <div className={cn('w-full space-y-4 p-1', className)}>
      <h3 className="text-sm font-semibold text-default-foreground">{title}</h3>

      {/* 填充 */}
      <div className="w-full space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium">填充</p>
          {!hasFill ? (
            <Button
              type="button"
              variant="ghost"
              isIconOnly
              size="sm"
              aria-label="添加填充"
              onMouseDown={(e) => e.preventDefault()}
              onPress={() => onChange({ fillColor: fillColorDefaultValue })}
            >
              <IconPlus className="h-4 w-4" stroke={1.5} />
            </Button>
          ) : null}
        </div>
        {hasFill && (
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <ColorPickerField
                aria-label="填充颜色"
                value={value.fillColor ?? fillColorDefaultValue}
                onChange={(next) => onChange({ fillColor: next })}
                variant="secondary"
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              isIconOnly
              size="sm"
              aria-label="移除填充"
              onMouseDown={(e) => e.preventDefault()}
              onPress={() => onChange({ fillColor: undefined })}
            >
              <IconMinus className="h-4 w-4" stroke={1.5} />
            </Button>
          </div>
        )}
      </div>

      {/* 描边 */}
      <div className="w-full space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium">描边</p>
          {!hasStroke ? (
            <Button
              variant="ghost"
              isIconOnly
              size="sm"
              aria-label="添加描边"
              onMouseDown={(e) => e.preventDefault()}
              onPress={addStroke}
            >
              <IconPlus stroke={1.5} />
            </Button>
          ) : null}
        </div>

        {hasStroke && stroke && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <ColorPickerField
                  aria-label="描边颜色"
                  value={stroke.color ?? strokeColorDefaultValue}
                  onChange={(next) => patchStroke({ color: next })}
                  variant="secondary"
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                isIconOnly
                size="sm"
                aria-label="移除描边"
                onMouseDown={(e) => e.preventDefault()}
                onPress={() => onChange({ stroke: undefined })}
              >
                <IconMinus className="h-4 w-4" stroke={1.5} />
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <AppearanceFieldRow label="线型">
                <Select
                  aria-label="描边线型"
                  variant="secondary"
                  value={currentStrokeStyle}
                  onChange={(key) => {
                    if (key == null) return;
                    patchStroke({ style: String(key) as StrokeStyle });
                  }}
                >
                  <Select.Trigger>
                    <span className="flex min-w-0 flex-1 items-center gap-1.5 truncate">
                      <span className="truncate">{strokeStyleLabel}</span>
                      {strokeLinePreview(currentStrokeStyle)}
                    </span>
                    <Select.Indicator />
                  </Select.Trigger>
                  <Select.Popover>
                    <ListBox>
                      {STROKE_STYLE_OPTIONS.map((opt) => (
                        <ListBox.Item key={opt.value} id={opt.value} textValue={opt.label}>
                          <span className="flex min-w-0 flex-1 items-center gap-2">
                            <span className="truncate">{opt.label}</span>
                            {strokeLinePreview(opt.value)}
                          </span>
                          <ListBox.ItemIndicator />
                        </ListBox.Item>
                      ))}
                    </ListBox>
                  </Select.Popover>
                </Select>
              </AppearanceFieldRow>

              <AppearanceFieldRow label="线宽">
                <NumberStepper
                  aria-label="描边线宽"
                  size="sm"
                  value={stroke.width ?? strokeWidthDefaultValue}
                  minValue={0}
                  maxValue={strokeWidthMax}
                  step={1}
                  onChange={(v) => {
                    if (v == null || Number.isNaN(v)) return;
                    patchStroke({ width: Math.max(0, Math.round(v)) });
                  }}
                >
                  <NumberStepper.Group>
                    <NumberStepper.DecrementButton />
                    <NumberStepper.Value />
                    <NumberStepper.IncrementButton />
                  </NumberStepper.Group>
                </NumberStepper>
              </AppearanceFieldRow>

              <AppearanceFieldRow label="位置">
                <Select
                  aria-label="描边位置"
                  variant="secondary"
                  value={currentPlacement}
                  onChange={(key) => {
                    if (key == null) return;
                    patchStroke({ placement: String(key) as StrokePlacement });
                  }}
                >
                  <Select.Trigger>
                    <Select.Value>{placementLabel}</Select.Value>
                    <Select.Indicator />
                  </Select.Trigger>
                  <Select.Popover>
                    <ListBox>
                      {STROKE_PLACEMENT_OPTIONS.map((opt) => (
                        <ListBox.Item key={opt.value} id={opt.value} textValue={opt.label}>
                          {opt.label}
                          <ListBox.ItemIndicator />
                        </ListBox.Item>
                      ))}
                    </ListBox>
                  </Select.Popover>
                </Select>
              </AppearanceFieldRow>

              <AppearanceFieldRow label="透明度">
                <NumberStepper
                  aria-label="描边透明度"
                  size="sm"
                  value={stroke.opacity ?? strokeOpacityDefaultValue}
                  minValue={0}
                  maxValue={1}
                  step={0.1}
                  onChange={(v) => {
                    if (v == null || Number.isNaN(v)) return;
                    patchStroke({ opacity: Math.min(1, Math.max(0, v)) });
                  }}
                >
                  <NumberStepper.Group>
                    <NumberStepper.DecrementButton />
                    <NumberStepper.Value />
                    <NumberStepper.IncrementButton />
                  </NumberStepper.Group>
                </NumberStepper>
              </AppearanceFieldRow>
            </div>
          </div>
        )}
      </div>

      {/* 圆角 */}
      <div className="w-full space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium">圆角</p>
          {!hasRadius ? (
            <Button
              variant="ghost"
              isIconOnly
              size="sm"
              aria-label="添加圆角"
              onMouseDown={(e) => e.preventDefault()}
              onPress={() => {
                setCornersExpanded(false);
                onChange({ radius: { all: radiusDefaultValue } });
              }}
            >
              <IconPlus stroke={1.5} />
            </Button>
          ) : cornersExpanded ? (
            <Button
              variant="ghost"
              isIconOnly
              size="sm"
              aria-label="移除圆角"
              onMouseDown={(e) => e.preventDefault()}
              onPress={() => {
                setCornersExpanded(false);
                onChange({ radius: undefined });
              }}
            >
              <IconMinus className="h-4 w-4" stroke={1.5} />
            </Button>
          ) : null}
        </div>

        {hasRadius && radius && resolvedCorners && (
          <>
            {!cornersExpanded ? (
              <div className="flex items-center gap-2">
                <div className="min-w-0 flex-1">
                  <InputGroup aria-label="四角统一圆角（像素）" variant="secondary">
                    <InputGroup.Prefix>
                      <IconRadiusTopLeft className="size-4 shrink-0" stroke={1.5} aria-hidden />
                    </InputGroup.Prefix>
                    <InputGroup.Input
                      type="number"
                      min={0}
                      value={uniformCornerRadiusValue(radius)}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        onChange({ radius: { all: Math.max(0, Math.round(num(e.target.value))) } })
                      }
                      className="w-full text-left"
                    />
                  </InputGroup>
                </div>
                <Button
                  variant="ghost"
                  isIconOnly
                  size="sm"
                  aria-label="分别设置四角圆角"
                  onMouseDown={(e) => e.preventDefault()}
                  onPress={toggleCornersExpanded}
                >
                  <IconBorderCorners className="h-4 w-4" stroke={1.5} />
                </Button>
                <Button
                  variant="ghost"
                  isIconOnly
                  size="sm"
                  aria-label="移除圆角"
                  onMouseDown={(e) => e.preventDefault()}
                  onPress={() => {
                    setCornersExpanded(false);
                    onChange({ radius: undefined });
                  }}
                >
                  <IconMinus className="h-4 w-4" stroke={1.5} />
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-[1fr_1fr_auto] grid-rows-2 gap-2">
                <CornerRadiusInput
                  ariaLabel="左上圆角（像素）"
                  Icon={IconRadiusTopLeft}
                  value={resolvedCorners.tl}
                  onChange={(next) => patchCorner('topLeft', next)}
                />
                <CornerRadiusInput
                  ariaLabel="右上圆角（像素）"
                  Icon={IconRadiusTopRight}
                  value={resolvedCorners.tr}
                  onChange={(next) => patchCorner('topRight', next)}
                />
                <div className="row-span-2 self-start">
                  <Button
                    type="button"
                    variant="ghost"
                    isIconOnly
                    size="sm"
                    aria-label="统一圆角"
                    onMouseDown={(e) => e.preventDefault()}
                    onPress={toggleCornersExpanded}
                  >
                    <IconMinus className="h-4 w-4" stroke={1.5} />
                  </Button>
                </div>
                <CornerRadiusInput
                  ariaLabel="左下圆角（像素）"
                  Icon={IconRadiusBottomLeft}
                  value={resolvedCorners.bl}
                  onChange={(next) => patchCorner('bottomLeft', next)}
                />
                <CornerRadiusInput
                  ariaLabel="右下圆角（像素）"
                  Icon={IconRadiusBottomRight}
                  value={resolvedCorners.br}
                  onChange={(next) => patchCorner('bottomRight', next)}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* 透明度 */}
      <div className="w-full space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium">透明度</p>
          {!hasOpacity ? (
            <Button
              variant="ghost"
              isIconOnly
              size="sm"
              aria-label="添加透明度"
              onMouseDown={(e) => e.preventDefault()}
              onPress={() => onChange({ opacity: opacityAddDefaultValue })}
            >
              <IconPlus stroke={1.5} />
            </Button>
          ) : null}
        </div>

        {hasOpacity && (
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <CellSlider
                aria-label="透明度"
                variant="secondary"
                value={opacity}
                minValue={0}
                maxValue={1}
                step={opacityStep}
                onChange={(v) => {
                  const next = Array.isArray(v) ? v[0] : v;
                  if (next == null || Number.isNaN(next)) return;
                  onChange({ opacity: clampOpacity(next) });
                }}
              >
                <CellSlider.Track>
                  <CellSlider.Output />
                  <CellSlider.Fill />
                  <CellSlider.Thumb />
                </CellSlider.Track>
              </CellSlider>
            </div>
            <Button
              type="button"
              variant="ghost"
              isIconOnly
              size="sm"
              aria-label="移除透明度"
              onMouseDown={(e) => e.preventDefault()}
              onPress={() => onChange({ opacity: undefined })}
            >
              <IconMinus className="h-4 w-4" stroke={1.5} />
            </Button>
          </div>
        )}
      </div>

      {/* 阴影 */}
      <div className="w-full space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium">阴影</p>
          {!hasShadow ? (
            <Button
              type="button"
              variant="ghost"
              isIconOnly
              size="sm"
              aria-label="添加阴影"
              onMouseDown={(e) => e.preventDefault()}
              onPress={() => onChange({ shadow: shadowAddDefaultValue })}
            >
              <IconPlus className="h-4 w-4" stroke={1.5} />
            </Button>
          ) : null}
        </div>

        {hasShadow && shadow && (
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <Select
                aria-label="阴影类型"
                variant="secondary"
                value={shadow.type}
                onChange={(key) => {
                  if (key == null) return;
                  patchShadow({ type: String(key) as AppearanceShadowValue['type'] });
                }}
              >
                <Select.Trigger>
                  <Select.Value />
                  <Select.Indicator />
                </Select.Trigger>
              <Select.Popover>
                <ListBox>
                  {SHADOW_TYPE_OPTIONS.map((opt) => (
                    <ListBox.Item key={opt.value} id={opt.value} textValue={opt.label}>
                      {opt.label}
                      <ListBox.ItemIndicator />
                    </ListBox.Item>
                  ))}
                </ListBox>
              </Select.Popover>
              </Select>
            </div>

            <Popover>
              <Popover.Trigger>
                <Button
                  type="button"
                  variant="ghost"
                  isIconOnly
                  size="sm"
                  aria-label="阴影设置"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <IconAdjustments className="h-4 w-4" stroke={1.5} />
                </Button>
              </Popover.Trigger>
              <Popover.Content placement="bottom end" offset={6}>
                <Popover.Dialog>
                  <ShadowSettingsPopover
                    value={shadow}
                    onChange={patchShadow}
                    opacityStep={opacityStep}
                  />
                </Popover.Dialog>
              </Popover.Content>
            </Popover>

            <Button
              type="button"
              variant="ghost"
              isIconOnly
              size="sm"
              aria-label="移除阴影"
              onMouseDown={(e) => e.preventDefault()}
              onPress={() => onChange({ shadow: undefined })}
            >
              <IconMinus className="h-4 w-4" stroke={1.5} />
            </Button>
          </div>
        )}
      </div>

      {/* 模糊 */}
      <div className="w-full space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium">模糊</p>
          {!hasBlur ? (
            <Button
              type="button"
              variant="ghost"
              isIconOnly
              size="sm"
              aria-label="添加模糊"
              onMouseDown={(e) => e.preventDefault()}
              onPress={() => onChange({ blurPx: blurAddDefaultValue })}
            >
              <IconPlus className="h-4 w-4" stroke={1.5} />
            </Button>
          ) : null}
        </div>

        {hasBlur && (
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <CellSlider
                aria-label="模糊"
                variant="secondary"
                value={blurPx}
                minValue={0}
                maxValue={blurMax}
                step={blurStep}
                onChange={(v) => {
                  const next = Array.isArray(v) ? v[0] : v;
                  if (next == null || Number.isNaN(next)) return;
                  onChange({ blurPx: clampBlur(next, blurMax) });
                }}
              >
                <CellSlider.Track>
                  <CellSlider.Output />
                  <CellSlider.Fill />
                  <CellSlider.Thumb />
                </CellSlider.Track>
              </CellSlider>
            </div>
            <Button
              type="button"
              variant="ghost"
              isIconOnly
              size="sm"
              aria-label="移除模糊"
              onMouseDown={(e) => e.preventDefault()}
              onPress={() => onChange({ blurPx: undefined })}
            >
              <IconMinus className="h-4 w-4" stroke={1.5} />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
